import i18next from 'i18next'
import { ConformedBranding } from '../../../views'

export type BrandSetValidationResult = {
	isValid: boolean
	errors: string[]
}

/**
 * Validates a brand set according to the requirements:
 * - Must have at least one palette with 4 colors (foreground, background, accentForeground, accentBackground)
 * - Tone of voice and visual guidelines are optional
 */
export function validateBrandSet(branding: ConformedBranding | null | undefined): BrandSetValidationResult {
	const errors: string[] = []

	if (!branding) {
		errors.push(i18next.t('editor:brandSet.noBrandSelected'))
		return { isValid: false, errors }
	}

	// Check that the branding has all 4 required colors
	const hasValidPalette = branding.foreground
		&& branding.background
		&& branding.accentForeground
		&& branding.accentBackground

	if (!hasValidPalette) {
		errors.push(i18next.t('editor:brandSet.needCompletePalette'))
	}

	return {
		isValid: errors.length === 0,
		errors,
	}
}

/**
 * Checks if brand library is ready (has loaded and has at least one brand set)
 */
export function isBrandLibraryReady(brandLibrary: ConformedBranding[], isLoading: boolean): boolean {
	return !isLoading && brandLibrary.length > 0
}

/**
 * Gets a human-readable error message for brand validation failures
 */
export function getBrandValidationErrorMessage(
	brandLibrary: ConformedBranding[],
	selectedBrand: ConformedBranding | null,
	isLoading: boolean,
): string | null {
	if (isLoading) {
		return null
	}

	if (brandLibrary.length === 0 && !selectedBrand) {
		return i18next.t('editor:brandSet.noBrandsAvailable')
	}

	if (!selectedBrand) {
		return i18next.t('editor:brandSet.selectBrand')
	}

	const validation = validateBrandSet(selectedBrand)
	if (!validation.isValid) {
		return validation.errors[0] ?? i18next.t('editor:brandSet.invalid')
	}

	return null
}
